import React, { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookOpen } from 'lucide-react';
import apiService from '@/services/api';

export default function ClassSelector({ onClassChange, currentClass }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedClass, setSelectedClass] = useState(currentClass || '');

  useEffect(() => {
    fetchTeacherClasses();
  }, []);

  const fetchTeacherClasses = async (retryCount = 0) => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await apiService.getTeacherClasses();
      setClasses(data || []);
      
      // Auto-select first class if none selected
      if (!selectedClass && data && data.length > 0) {
        const firstClass = data[0];
        setSelectedClass(firstClass.id);
        if (onClassChange) {
          onClassChange(firstClass);
        }
      }
      // If API succeeded but returned empty, auto-retry once after a delay (cold start may have caused empty response)
      if ((!data || data.length === 0) && retryCount === 0) {
        setTimeout(() => fetchTeacherClasses(1), 3000);
        return;
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setLoadError(true);
      // Auto-retry once after cold start delay
      if (retryCount === 0) {
        setTimeout(() => fetchTeacherClasses(1), 3000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    const selectedClassData = classes.find(c => c.id === classId);
    if (onClassChange && selectedClassData) {
      onClassChange(selectedClassData);
    }
  };

  // Group classes by year
  const groupedClasses = classes.reduce((acc, cls) => {
    const year = cls.year;
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(cls);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/50">
        <BookOpen className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading classes...</span>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/50">
        <BookOpen className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {loadError ? 'Failed to load classes' : 'No classes assigned'}
        </span>
        {loadError && (
          <button
            onClick={fetchTeacherClasses}
            className="text-xs text-blue-600 hover:text-blue-800 underline ml-1"
            data-testid="retry-classes-btn"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <Select value={selectedClass} onValueChange={handleClassChange}>
      <SelectTrigger className="w-full max-w-md">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          <SelectValue placeholder="Select a class" />
        </div>
      </SelectTrigger>
      <SelectContent>
        {Object.keys(groupedClasses).sort().map(year => (
          <SelectGroup key={year}>
            <SelectLabel>Year {year}</SelectLabel>
            {groupedClasses[year].map(cls => (
              <SelectItem key={cls.id} value={cls.id}>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{cls.subject}</span>
                  <span className="text-xs text-muted-foreground">
                    ({cls.name})
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
